'use client';

import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Buildings,
  Code,
  ChartLineUp,
  Check,
  ArrowRight,
  TrendUp,
  Users,
  PresentationChart,
  Envelope,
  Globe,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { useSanityPage } from '@/lib/sanity.hooks';
import { AnimatedSection } from '@/components/marketing/AnimatedSection';

const featureCards = [
  {
    icon: Buildings,
    title: 'White-label portal',
    description:
      "Remove Rooflyzer branding and apply your agency's logo and colors. Your clients see a premium dashboard that looks like you built it.",
  },
  {
    icon: Code,
    title: 'Lead API & webhooks',
    description:
      "Push leads from Facebook, Google Ads, custom landing pages, and web forms directly into your client's Rooflyzer CRM via API or webhook.",
  },
  {
    icon: ChartLineUp,
    title: 'Agency master view',
    description:
      'Track all your contractor clients in one secure dashboard. Monitor revenue generated, cost per lead, customer acquisition cost, and retention.',
  },
];

const agencyUseCases = [
  'Resell a branded CRM platform under your own pricing and packaging',
  'Track exact marketing performance across every client account',
  'Provide lead attribution without exposing raw CRM tools',
  'Generate white-label reports that prove ROI every month',
];

const contractorUseCases = [
  'Run their own CRM, proposals, invoicing, and project management',
  'Get full attribution from ad click to signed contract',
  'Use built-in storm intelligence and canvassing tools',
  'Scale without adding five separate software subscriptions',
];

export const AgenciesPage = () => {
  const { data: cmsData } = useSanityPage('agencies');
  const reduced = useReducedMotion();

  const pageTitle = cmsData?.seo?.metaTitle || 'Roofing Marketing Agency Tools — White-Label CRM';
  const pageDescription =
    cmsData?.seo?.metaDescription ||
    cmsData?.description ||
    'Offer your roofing contractor clients a complete CRM platform with attribution tracking, white-label reports, and an agency master dashboard.';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/agencies"
        keywords="roofing marketing agency, roofing white label CRM, roofing agency software, contractor lead attribution"
        ogImage={cmsData?.seo?.ogImage?.asset?.url || '/social-card.svg'}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'For Agencies',
              item: 'https://rooflyzer.pro/agencies',
            },
          ],
        }}
      />

      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider mb-6">
              <Users className="w-4 h-4 text-emerald-400" weight="fill" />
              <span>Agency Partner Program</span>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <h1 className="hero-h1 font-bold text-white mb-5">
              The White-Label CRM <span className="text-white">Built for Agencies.</span>
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <p className="hero-lead text-white/65 max-w-2xl mx-auto mb-8">
              Stop patching together spreadsheets and dashboards. Give your roofing contractor
              clients a complete operating system with attribution tracking that proves your value.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                asChild
              >
                <Link to="/contact">
                  Become a partner
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-lg border border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800/60 px-8"
                asChild
              >
                <Link to="/demo">See a demo</Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {featureCards.map((item, i) => {
              const Icon = item.icon;
              return (
                <AnimatedSection key={item.title} delay={i * 0.1}>
                  <div className="h-full rounded-3xl bg-white/[0.03] border border-slate-800 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-slate-700">
                    <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-emerald-400" weight="bold" />
                    </div>
                    <h3 className="section-h3 font-bold text-white mb-4">{item.title}</h3>
                    <p className="body-lg text-white/60 leading-relaxed">{item.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider mb-4">
              <TrendUp className="w-4 h-4 text-emerald-400" />
              <span>Solution fit</span>
            </div>
            <h2 className="section-h2 font-bold text-white mb-3">
              Agency tools vs full contractor stack
            </h2>
            <p className="body-lg text-white/55">
              Choose the model that matches your business. Both use the same platform, data, and
              attribution engine.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6">
            <AnimatedSection delay={0.1}>
              <div className="h-full rounded-3xl bg-white/[0.03] border border-slate-800 p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">
                  Agency white-label
                </h3>
                <p className="text-white/70 mb-6 leading-relaxed">
                  For marketing agencies that want to offer branded lead tracking, attribution, and
                  reporting dashboards directly to their contractor clients.
                </p>
                <ul className="space-y-3.5 text-white/80 text-sm">
                  {agencyUseCases.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check
                        className="size-5 text-emerald-400 flex-shrink-0 mt-0.5"
                        weight="bold"
                      />
                      <span className="body-lg text-white/85">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="h-full rounded-3xl bg-white/[0.03] border border-slate-800 p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">
                  Full contractor stack
                </h3>
                <p className="text-white/70 mb-6 leading-relaxed">
                  For contractors that want the full Rooflyzer OS experience: CRM, estimates,
                  proposals, invoicing, project management, and marketing automation.
                </p>
                <ul className="space-y-3.5 text-white/80 text-sm">
                  {contractorUseCases.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check
                        className="size-5 text-emerald-400 flex-shrink-0 mt-0.5"
                        weight="bold"
                      />
                      <span className="body-lg text-white/85">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="rounded-3xl bg-white/[0.03] border border-white/[0.08] p-8 md:p-12 flex flex-col lg:flex-row items-center gap-10 overflow-hidden">
              <div className="flex-1 space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider">
                  <PresentationChart className="w-4 h-4 text-emerald-400" />
                  <span>Partner reporting</span>
                </div>
                <h2 className="section-h2 font-bold text-white">Prove ROI every month</h2>
                <p className="body-lg text-white/60">
                  Show clients exactly how many leads, booked appointments, and signed contracts
                  came from each campaign. Export white-label reports or give clients read-only
                  dashboard access.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                    asChild
                  >
                    <Link to="/contact">
                      <Envelope className="mr-2 size-5" />
                      Become a partner
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-lg border border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800/60 px-8"
                    asChild
                  >
                    <Link to="/integrations">
                      <Globe className="mr-2 size-5" />
                      Explore integrations
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="lg:w-2/5">
                <div className="rounded-2xl overflow-hidden border border-white/[0.08]">
                  <img
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=1600&auto=format&fit=crop"
                    alt="Marketing team reviewing campaign analytics"
                    className="w-full h-56 object-cover opacity-80"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-3xl bg-white/[0.03] border border-white/[0.08] p-8 md:p-12 text-center space-y-6"
          >
            <h2 className="section-h2 font-bold text-white">
              Ready to add a CRM to your agency offer?
            </h2>
            <p className="body-lg text-white/60 max-w-xl mx-auto">
              Talk to our partnership team about white-label pricing, onboarding, and reporting.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                asChild
              >
                <Link to="/contact">
                  Apply to partner
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-lg border border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800/60 px-8"
                asChild
              >
                <Link to="/demo">Book a demo</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};
